// src/utils/timezone.ts

// Mapping of timezones to user-friendly names.
export const timezones: Record<string, string> = {
	'Pacific/Honolulu': 'Hawaii Time',
	'America/Anchorage': 'Alaska Time',
	'America/Los_Angeles': 'Pacific Time',
	'America/Denver': 'Mountain Time',
	'America/Chicago': 'Central Time',
	'America/New_York': 'Eastern Time',
};

// Mapping of timezones to their abbreviations.
export const timezoneAbbreviations: Record<string, string> = {
	'America/Los_Angeles': 'PST',
	'America/Chicago': 'CST',
	'America/New_York': 'EST',
	'America/Denver': 'MST',
	'America/Phoenix': 'MST',
	'America/Anchorage': 'AKST',
	'America/Honolulu': 'HST',
	'Pacific/Honolulu': 'HST',
};

// Returns a short timezone abbreviation if available.
export const getShortTZ = (timezone: string): string => {
	return timezoneAbbreviations[timezone] || timezone;
};

// Detects the user's local timezone (e.g. 'America/Chicago').
// If detection fails, returns undefined.
export const getUserLocalTimezone = (): string | undefined => {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return tz || undefined;
	} catch (error) {
		console.error('Error detecting timezone:', error);
		return undefined;
	}
};

// Detects the user's local timezone abbreviation (e.g. 'CST').
// If detection fails, returns undefined.
export const getUserLocalTimezoneAbbr = (): string | undefined => {
	try {
		const locale = navigator.language;
		const abbrMatch = /.*\s(.+)/.exec(new Date().toLocaleDateString(locale, { timeZoneName: 'short' }));
		if (abbrMatch?.[1]) {
			return abbrMatch[1];
		}
	} catch (error) {
		console.error('Error detecting timezone abbreviation:', error);
	}
	try {
		const match = new Date().toTimeString().match(/[A-Z](?!.*[(])/g);
		if (match) {
			return match.join('');
		}
	} catch (error) {
		console.error('Error detecting timezone abbreviation from time string:', error);
	}
	return undefined;
};

/**
 * Returns the effective timezone based on a custom (profile) value if provided;
 * otherwise, it returns the detected user local timezone.
 * If detection fails, it returns undefined.
 */
export const getEffectiveTimezone = (customTimezone?: string): string | undefined => {
	return customTimezone || getUserLocalTimezone();
};

/**
 * Returns the effective timezone abbreviation based on a custom (profile) value if provided;
 * otherwise, it returns the detected user local timezone abbreviation.
 * If detection fails, it returns undefined.
 */
export const getEffectiveTimezoneAbbr = (customTimezone?: string): string | undefined => {
	if (customTimezone) {
		return getShortTZ(customTimezone);
	}
	return getUserLocalTimezoneAbbr();
};
